import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

import type { GroupRowProps } from './GroupRow';
import { GroupRow } from './GroupRow';

const messages = defineMessages({
  pages: {
    id: 'autoCuration.pdfSplitEditor.pages',
    defaultMessage: 'Pages {first}-{last}',
  },
  removePages: {
    id: 'autoCuration.pdfSplitEditor.removePages',
    defaultMessage: 'Remove page(s)',
  },
  mergeWithAnyPage: {
    id: 'autoCuration.pdfSplitEditor.mergeWithAnyPage',
    defaultMessage: 'Merge with any page',
  },
});

interface PageGroupProps extends GroupRowProps {
  groupTitle: string;
  selected: boolean;
  isExpanded: boolean;
  onRemovePage: (page: number) => void;
  onExpand: (groupIndex: number | false) => void;
  onMergeWithAdjacent: (groupIndex: number, dir: 'next' | 'prev') => void;
  onOpenMergeMenu: (groupIndex: number, e: React.MouseEvent) => void;
}
export function PageGroup({
  currentPage,
  group,
  groupIndex,
  selected: isCurrentInGroup,
  isExpanded,
  prevGroup,
  nextGroup,
  canSplitPrev,
  canSplitNext,
  groupTitle,
  onPageClick,
  onRemovePage,
  onExpand,
  onMergeWithAdjacent,
  onSplitPage,
  onOpenMergeMenu,
}: PageGroupProps) {
  const { formatMessage } = useIntl();
  const firstPage = group[0];
  const lastPage = group[group.length - 1];

  const handleMergeNext = (groupIndex: number) => {
    onMergeWithAdjacent(groupIndex, 'next');
  };

  const handleMergePrevious = (groupIndex: number) => {
    onMergeWithAdjacent(groupIndex, 'prev');
  };

  const handleClick = () => {
    if (isCurrentInGroup) {
      return;
    }
    onPageClick(firstPage);
  };

  return (
    <Accordion
      key={groupIndex}
      expanded={isExpanded}
      onChange={(_, isExpanded) => onExpand(isExpanded ? groupIndex : false)}
      sx={{
        borderRadius: '8px',
        border: isCurrentInGroup ? '2px solid' : '1px solid',
        borderColor: isCurrentInGroup ? 'primary.main' : 'divider',
        boxShadow: isCurrentInGroup
          ? '0 0 12px rgba(59, 130, 246, 0.2)'
          : 'none',
        '&:before': { display: 'none' },
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={
          <Icon
            name="chevron-down"
            type="fas"
            color="primary.main"
            style={{ fontSize: 18 }}
          />
        }
        onClick={handleClick}
        sx={{
          minHeight: 48,
          bgcolor: isCurrentInGroup ? 'primary.50' : 'transparent',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: isCurrentInGroup ? 'primary.100' : 'grey.50',
          },
          '& .MuiAccordionSummary-content': {
            my: 1,
          },
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ width: '100%' }}
        >
          <Icon
            color={isCurrentInGroup ? 'primary.main' : 'action.main'}
            name="file-lines"
            type="fas"
            style={{ fontSize: 20 }}
          />

          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: isCurrentInGroup ? 600 : 500,
                  lineHeight: 1.2,
                }}
              >
                {groupTitle}
              </Typography>
            </Box>
            {group.length > 1 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  lineHeight: 1.2,
                  fontSize: '0.7rem',
                  display: 'block',
                  mt: 0.25,
                }}
              >
                {formatMessage(messages.pages, {
                  first: firstPage,
                  last: lastPage,
                })}
              </Typography>
            )}
          </Box>
          {/* Merge with any page and Delete buttons */}
          {isExpanded && (
            <Stack direction="row" spacing={1} sx={{ px: 1 }}>
              {/* Plus icon - show for all expanded groups */}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenMergeMenu(groupIndex, e);
                }}
                size="small"
                title={formatMessage(messages.mergeWithAnyPage)}
                sx={{
                  bgcolor: 'white',
                  color: (theme) => theme.palette.success.main,
                  border: '1px solid',
                  borderColor: (theme) => theme.palette.success.main,
                  borderRadius: '4px',
                  width: '20px',
                  height: '20px',
                  minWidth: '20px',
                  p: 0,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: (theme) => theme.palette.success.main,
                    borderColor: (theme) => theme.palette.success.main,
                    color: 'white',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <Icon name="plus" type="fas" style={{ fontSize: 14 }} />
              </IconButton>
              {/* Trash icon - only show for single-page groups */}
              {group.length === 1 && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    // Remove all pages in this group
                    group.forEach(onRemovePage);
                  }}
                  size="small"
                  title={formatMessage(messages.removePages)}
                  sx={{
                    bgcolor: 'white',
                    color: (theme) => theme.palette.error.main,
                    border: '1px solid',
                    borderColor: (theme) => theme.palette.error.main,
                    borderRadius: '4px',
                    width: '20px',
                    height: '20px',
                    minWidth: '20px',
                    p: 0,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: (theme) => theme.palette.error.main,
                      borderColor: (theme) => theme.palette.error.main,
                      color: 'white',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Icon name="trash" type="fas" style={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Stack>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ py: 1.5, px: 2 }}>
        {/* Individual pages in group */}
        <Stack spacing={0.75}>
          {group.map((pageNum, pageIndexInGroup) => {
            return (
              <GroupRow
                key={pageNum}
                currentPage={currentPage}
                group={group}
                pageIndexInGroup={pageIndexInGroup}
                pageNum={pageNum}
                groupIndex={groupIndex}
                canSplitNext={canSplitNext}
                canSplitPrev={canSplitPrev}
                prevGroup={prevGroup}
                nextGroup={nextGroup}
                onMergeNext={handleMergeNext}
                onMergePrevious={handleMergePrevious}
                onSplitPage={onSplitPage}
                onPageClick={onPageClick}
              />
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
